import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { apiErrorMessage } from "@/lib/api";
import { useCreateStaff } from "@/hooks/api/staff";

const schema = z.object({
  email: z.string().email("Invalid email"),
  display_name: z.string().optional(),
  password: z.string().min(10, "At least 10 characters"),
});

type FormValues = z.infer<typeof schema>;

function generatePassword(): string {
  // Not cryptographically reviewed UX polish — the backend still validates
  // strength server-side. Good enough for "click to fill a strong default."
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(36).padStart(2, "0")).join("").slice(0, 16);
}

export default function StaffFormPage() {
  const navigate = useNavigate();
  const createStaff = useCreateStaff();
  const [created, setCreated] = useState<{ email: string; password: string } | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", display_name: "", password: "" },
  });

  const onSubmit = async (data: FormValues) => {
    try {
      const result = await createStaff.mutateAsync(data);
      setCreated({ email: result.user.email, password: result.password });
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't create that account."));
    }
  };

  const copyAndClose = async () => {
    if (created) {
      await navigator.clipboard.writeText(
        `Email: ${created.email}\nPassword: ${created.password}`
      );
      toast.success("Copied credentials to clipboard.");
    }
    navigate("/staff", { replace: true });
  };

  if (created) {
    return (
      <AppShell>
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Account created</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">
              Share these credentials with {created.email} — the password is shown
              only once and can't be retrieved later. Use "Reset password" from the
              staff list if it's lost.
            </p>
            <div className="rounded-md border border-slate-200 bg-surface-alt p-3 font-mono text-sm">
              <div>{created.email}</div>
              <div>{created.password}</div>
            </div>
            <Button onClick={copyAndClose} className="w-full">
              Copy to clipboard &amp; done
            </Button>
          </CardContent>
        </Card>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <Card className="max-w-md">
        <CardHeader>
          <CardTitle>Add staff</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" autoComplete="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="display_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Initial password</FormLabel>
                    <div className="flex gap-2">
                      <FormControl>
                        <Input type="text" autoComplete="new-password" {...field} />
                      </FormControl>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => form.setValue("password", generatePassword())}
                      >
                        Generate
                      </Button>
                    </div>
                    <FormDescription>
                      Shown once after creation — share it with the new agent directly.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full" loading={createStaff.isPending}>
                Create account
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </AppShell>
  );
}
